import { useOutletContext } from 'react-router-dom';
import { Alert, Paper } from "@mui/material";

import Page from '~/components/Page';
import AdminSetDebtModal from '~/modals/AdminSetDebtModal';
import { useTrans } from "~/hooks/useTrans";
import { getProjectBreadcrumb } from "~/utils/breadcrumbs";
import { apiGetProject, apiGetProjectBillingHistory } from "~/providers/api";
import { IClientProject } from "~/providers/client-interfaces";
import { IOutlet, IPage } from "~/interfaces";
import ErrorInfo from '~/components/ErrorInfo';
import BillingHistoryTable from '~/tables/BillingHistoryTable';
import { IAdminProject } from "~/providers/admin-interfaces";
import { IProjectBillingHistory } from "~/providers/interfaces";
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import { DISABLE_BILLING_TABLE } from "~/const";

import "./style.less";

interface IState {
	loading: boolean;
	errorMsg: string;
	project: IClientProject | IAdminProject;
	billingHistory: Array<IProjectBillingHistory>;
	setDebt: IProjectBillingHistory | null;
}

const INIT_STATE: IState = {
	loading: true,
	errorMsg: "",
	project: null,
	billingHistory: [],
	setDebt: null,
};

export default function ProjectConsumptionHistoryPage({
	isInternal = false,
}: IPage) {
	const { trans } = useTrans();
	const { lang, isCzech } = useLang();
	const { state, updateState } = myUseState<IState>(INIT_STATE);
	const { params }: IOutlet = useOutletContext();
	const disableBillingTable = !isInternal && DISABLE_BILLING_TABLE;

	function onSwitchDebt(setDebt: IProjectBillingHistory) {
		updateState({
			setDebt,
		});
	}

	async function onSetDebthSave() {
		updateState({
			setDebt: null,
			loading: true,
		});

		const consumptionListData = await apiGetProjectBillingHistory(isInternal ? "admin" : "client", params.projectId, lang);

		updateState({
			loading: false,
			...consumptionListData.noErrors
				? {
					billingHistory: consumptionListData.data,
				}
				: {},
			errorMsg: [
				...consumptionListData.errors,
			].join(", "),
		});
	}

	async function loadData() {
		updateState(INIT_STATE);

		const [projectData, consumptionListData] = await Promise.all([
			apiGetProject(isInternal ? "admin" : "client", params.projectId, lang),
			apiGetProjectBillingHistory(isInternal ? "admin" : "client", params.projectId, lang),
		]);

		updateState({
			loading: false,
			...projectData.noErrors
				? {
					project: projectData.data,
				}
				: {},
			...consumptionListData.noErrors
				? {
					billingHistory: consumptionListData.data,
				}
				: {},
			errorMsg: [
				...projectData.errors,
				...consumptionListData.errors,
			].join(", "),
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	return <Page checkLogged={true} title={trans("menu.consumption")} breadcrumbs={getProjectBreadcrumb(trans, isInternal, state.project, trans("menu.consumption"))} checkInternalRole={isInternal ? "salesman" : "none"}
		onLoad={onPageLoad} seoTitleTrans="seo.title.consumption.history">
		<Paper elevation={3} className="projectConsumptionHistoryPage__paper">
			{ !disableBillingTable && <BillingHistoryTable items={state.billingHistory} loading={state.loading} isInternal={isInternal} params={params} isSOS={isInternal && state.project?.billingMethod === "sos"} onSwitchDebt={onSwitchDebt} /> }
			{ disableBillingTable && <Alert severity="info">
				{ isCzech ? "Probíhá měsíční uzávěrka" : "Mothly closure is in progress" }
			</Alert> }
		</Paper>
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
		{ state.setDebt && <AdminSetDebtModal billing={state.setDebt} params={params} onClose={() => updateState({ setDebt: null })} onSave={onSetDebthSave} /> }
	</Page>;
}
